// src/lib/horaires-impact.js — calculs PURS de l'écran « Demandes d'horaires »
// (orchestrateur) : fenêtre de ménage restante, clamp de la proposition,
// montants INDICATIFS.
//
// ⚠️ MIROIR CLIENT de lib/services-hourly.js (CJS serveur, non importable
// depuis src/ — CRA ModuleScopePlugin interdit les imports hors src/). Les
// fonctions parseHHMMToMinutes / billableHours / amountCentsFor sont
// RÉIMPLÉMENTÉES à l'identique ici, avec la MÊME sémantique (arrondi heure
// entamée, step 60 min). Toute divergence = bug d'affichage, jamais un bug
// d'argent : le montant affiché est INDICATIF, le serveur RECALCULE toujours
// (hours_granted × tarif SNAPSHOT) à la validation — cf. lib/services-grant.js.
//
// Seuil de ménage : défaut 90 min (décision Dimitri 2026-08-06), réglable PAR
// LOGEMENT (cfg `menage_duree_min`, source serveur lib/menage-duree.js — les
// deux défauts doivent rester alignés).

// Défaut produit — MIROIR de lib/menage-duree.js::DEFAULT_MENAGE_DUREE_MIN.
export const DEFAULT_MENAGE_DUREE_MIN = 90

// MIROIR de lib/services-hourly.js::HOURLY_BILLING_STEP_MINUTES (R7 : « heure
// entamée = heure due »).
export const BILLING_STEP_MINUTES = 30

/**
 * "HH:MM" (ou "HH:MM:SS") → minutes depuis minuit. null si invalide.
 * MIROIR de lib/services-hourly.js::parseHHMMToMinutes.
 */
export function parseHHMMToMinutes(hhmm) {
  if (typeof hhmm !== 'string') return null
  const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h * 60 + min
}

/** minutes depuis minuit → "HH:MM" (null si hors [0,1439]). */
export function formatMinutesToHHMM(minutes) {
  if (!Number.isFinite(minutes) || minutes < 0 || minutes > 1439) return null
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/**
 * Formate une durée en minutes façon écran : « 3 h 00 » / « 45 min ».
 * Durée négative (fenêtre impossible) → « 0 min » (le rouge porte l'alerte).
 */
export function formatDureeMin(minutes) {
  if (!Number.isFinite(minutes)) return '—'
  const m = Math.max(0, Math.round(minutes))
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const rest = m % 60
  return `${h} h ${String(rest).padStart(2, '0')}`
}

/**
 * FENÊTRE DE MÉNAGE restante (minutes) si on accorde l'heure `granted`.
 *
 *   - late_checkout  : de granted → check-in STANDARD du logement
 *                      (le ménage commence quand le voyageur part) ;
 *   - early_checkin  : du check-out STANDARD du logement → granted
 *                      (le ménage doit être fini quand le voyageur arrive).
 *
 * CUMUL MÊME JOUR (garde-fou Dimitri) : si une demande ADVERSE pend sur le même
 * logement / créneau (early_checkin ET late_checkout qui encadrent le même
 * ménage), passer son heure via `counterpart` : elle REMPLACE la borne standard
 * correspondante — la fenêtre tient alors compte des DEUX heures demandées.
 *
 * Retourne les minutes SIGNÉES (négatif = fenêtre impossible — l'écran passe au
 * rouge), ou null si une entrée est illisible (l'écran affiche « — », jamais un
 * chiffre inventé).
 *
 * @param {object} p
 * @param {'early_checkin'|'late_checkout'} p.event_kind
 * @param {string} p.granted - heure accordée envisagée "HH:MM"
 * @param {string} p.standard_checkin - check-in standard du logement "HH:MM"
 * @param {string} p.standard_checkout - check-out standard du logement "HH:MM"
 * @param {string|null} [p.counterpart] - heure de la demande ADVERSE pending
 *   (late→son early, early→son late) sur le même créneau, sinon null.
 * @returns {number|null} minutes (signées)
 */
export function computeMenageWindow({
  event_kind,
  granted,
  standard_checkin,
  standard_checkout,
  counterpart = null,
}) {
  const grantedMin = parseHHMMToMinutes(granted)
  const counterpartMin = counterpart != null ? parseHHMMToMinutes(counterpart) : null
  if (grantedMin == null) return null
  if (counterpart != null && counterpartMin == null) return null
  if (event_kind === 'late_checkout') {
    // Fin de fenêtre = check-in standard, SAUF arrivée anticipée pendante.
    const endMin = counterpartMin != null ? counterpartMin : parseHHMMToMinutes(standard_checkin)
    if (endMin == null) return null
    return endMin - grantedMin
  }
  if (event_kind === 'early_checkin') {
    // Début de fenêtre = check-out standard, SAUF départ tardif pendant.
    const startMin = counterpartMin != null ? counterpartMin : parseHHMMToMinutes(standard_checkout)
    if (startMin == null) return null
    return grantedMin - startMin
  }
  return null
}

/**
 * CLAMP de la proposition « JE PROPOSE » — miroir du sens de
 * lib/services-hourly.js::checkGrantWithinBounds (le serveur, lui, REJETTE hors
 * borne ; l'input, lui, RAMÈNE dans la borne pour ne jamais laisser partir une
 * valeur que le serveur refuserait) :
 *   - late_checkout : granted ∈ [standard, requested]
 *   - early_checkin : granted ∈ [requested, standard]
 *
 * @returns {number|null} minutes clampées (null si entrées illisibles)
 */
export function clampProposalMinutes(eventKind, proposedMin, standardMin, requestedMin) {
  if (
    !Number.isFinite(proposedMin) ||
    !Number.isFinite(standardMin) ||
    !Number.isFinite(requestedMin)
  ) {
    return null
  }
  const lo = eventKind === 'late_checkout' ? standardMin : requestedMin
  const hi = eventKind === 'late_checkout' ? requestedMin : standardMin
  if (lo > hi) return null // bornes incohérentes (booking mal formé) → pas de clamp inventé
  return Math.min(Math.max(proposedMin, lo), hi)
}

/**
 * Heures FACTURABLES entre une heure et le standard — MIROIR de
 * lib/services-hourly.js::billableHours (arrondi heure entamée, step 60).
 */
export function billableHours(timeMin, standardMin) {
  if (!Number.isFinite(timeMin) || !Number.isFinite(standardMin)) return 0
  const deltaMin = Math.abs(standardMin - timeMin)
  if (deltaMin <= 0) return 0
  return (Math.ceil(deltaMin / BILLING_STEP_MINUTES) * BILLING_STEP_MINUTES) / 60
}

/**
 * Montants INDICATIFS d'une proposition : capturé (heures facturables × tarif
 * snapshot) + libéré (différence avec l'empreinte). Le serveur recalcule — cet
 * affichage n'engage rien (libellé « montant recalculé côté serveur » à l'écran).
 *
 * @returns {{ captured_cents: number, released_cents: number, hours: number }|null}
 */
export function computeProposalAmounts({
  proposedMin,
  standardMin,
  tarif_per_hour_cents,
  hold_amount_cents,
}) {
  if (!Number.isFinite(proposedMin) || !Number.isFinite(standardMin)) return null
  const tarif = Number(tarif_per_hour_cents)
  if (!Number.isFinite(tarif) || tarif < 0) return null
  const hours = billableHours(proposedMin, standardMin)
  const captured = Math.ceil(hours * tarif)
  const hold = Number.isFinite(Number(hold_amount_cents)) ? Number(hold_amount_cents) : null
  const released = hold != null ? Math.max(0, hold - captured) : null
  return { captured_cents: captured, released_cents: released, hours }
}

/**
 * CUMUL même jour : trouve, dans la liste des demandes PENDING, la demande
 * ADVERSE (autre event_kind) sur le MÊME logement et la MÊME date d'événement
 * — deux demandes qui encadrent le même créneau de ménage.
 *
 * NB : pour un late_checkout du jour J, l'arrivée anticipée qui rogne la même
 * fenêtre est une early_checkin dont l'event_date est aussi J (le check-in du
 * voyageur suivant a lieu le jour du départ).
 *
 * @returns {object|null} le booking adverse, sinon null
 */
export function findCounterpartPending(booking, pendingList, grantedList) {
  if (!booking) return null
  const otherKind = booking.event_kind === 'late_checkout' ? 'early_checkin' : 'late_checkout'
  const correspond = (b) =>
    b &&
    b.id !== booking.id &&
    b.logement_id === booking.logement_id &&
    b.event_kind === otherKind &&
    b.event_date === booking.event_date

  // 🔴 LES DEMANDES DÉJÀ ACCORDÉES D'ABORD (correction 2026-08-06, revue
  // adversariale). Ne chercher que dans `pending` créait le pire défaut
  // possible sur cet écran : l'impact virait du ROUGE au VERT tout seul.
  //
  // Déroulé réel : deux demandes encadrent le même ménage, l'écran affiche
  // « 0 min » en rouge. L'orchestrateur accorde la première — elle quitte
  // `pending`. La seconde PERD sa borne, retombe sur l'horaire standard du
  // logement (la fenêtre la plus large possible) et s'affiche « 3 h, vert ».
  // Il accorde en confiance un créneau où le voyageur suivant entre AVANT que
  // le précédent soit sorti.
  //
  // Une demande accordée porte `granted_time` : c'est l'heure RÉELLE, donc une
  // borne plus juste que le `requested_time` d'une demande encore pendante.
  const accorde = Array.isArray(grantedList) ? grantedList.find(correspond) : null
  if (accorde) return { ...accorde, _borne: 'granted', _decidee: true }

  // Sinon la demande pendante, bornée par ce qui est DEMANDÉ — le pire cas,
  // ce qui est le bon défaut quand la décision n'est pas encore prise.
  const pendante = Array.isArray(pendingList) ? pendingList.find(correspond) : null
  if (pendante) return { ...pendante, _borne: 'requested', _decidee: false }
  return null
}

/** Centimes → « 12,50 € » (affichage écran). */
export function formatEurCents(cents) {
  // null / undefined / '' ⇒ « montant inconnu », PAS zéro. `Number(null)` vaut 0
  // et « 0,00 € » sur un écran d'argent se lit « rien à payer » — un contresens
  // quand la vérité est « on ne sait pas encore ».
  if (cents === null || cents === undefined || cents === '') return '— €'
  const n = Number(cents)
  if (!Number.isFinite(n)) return '— €'
  return `${(n / 100).toFixed(2).replace('.', ',')} €`
}

// ── Fuseau Europe/Paris (miroirs client de lib/services-hourly.js) ──────────
// Réimplémentés ici car lib/ (CJS serveur) n'est pas importable depuis src/
// (CRA ModuleScopePlugin). MÊME algorithme : offset Intl + double passe DST.

/** Décalage (ms) entre `tz` et UTC à l'instant `date` — miroir tzOffsetMs. */
function tzOffsetMs(date, tz) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  })
  const parts = dtf.formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc }, {})
  let hour = parseInt(parts.hour, 10)
  if (hour === 24) hour = 0
  const asUTC = Date.UTC(
    parseInt(parts.year, 10), parseInt(parts.month, 10) - 1, parseInt(parts.day, 10),
    hour, parseInt(parts.minute, 10), parseInt(parts.second, 10),
  )
  return asUTC - date.getTime()
}

/**
 * Heure « pendule » Europe/Paris (date civile + HH:MM) → epoch ms UTC.
 * Miroir de lib/services-hourly.js::parisWallClockToEpochMs (double passe DST).
 */
export function parisWallClockToEpochMs(dateISO, hhmm) {
  if (typeof dateISO !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) return null
  const min = parseHHMMToMinutes(hhmm)
  if (min == null) return null
  const [Y, M, D] = dateISO.split('-').map((x) => parseInt(x, 10))
  const naiveUtc = Date.UTC(Y, M - 1, D, Math.floor(min / 60), min % 60, 0)
  const offset = tzOffsetMs(new Date(naiveUtc), 'Europe/Paris')
  let epoch = naiveUtc - offset
  const offset2 = tzOffsetMs(new Date(epoch), 'Europe/Paris')
  if (offset2 !== offset) epoch = naiveUtc - offset2
  return epoch
}

/** Date civile d'AUJOURD'HUI en Europe/Paris ('YYYY-MM-DD'). */
export function parisTodayISO(now = new Date()) {
  return new Intl.DateTimeFormat('fr-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now)
}

/**
 * Arithmétique de date CIVILE (sans fuseau — une date civile Paris ± N jours
 * reste une date civile Paris). 'YYYY-MM-DD' + delta jours → 'YYYY-MM-DD'.
 */
export function addDaysISO(dateISO, delta) {
  if (typeof dateISO !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) return null
  const [Y, M, D] = dateISO.split('-').map((x) => parseInt(x, 10))
  const d = new Date(Date.UTC(Y, M - 1, D + delta))
  return d.toISOString().slice(0, 10)
}
